import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { globalTheme, useMixin } from '@clds/component-theme';
import { WIDGET_VIEW_TYPES } from '@cld/upload-widget-common';
import t from 'common/i18n';
import actions, { types as rootAts } from 'root/actions';
import LinkButton from '../../../root/components/LinkButton/LinkButton';
import Button from '../../../root/components/Button';
import { selectIsMultipleEnabled } from 'root/selectors';
import { selectShowCompletedButton, selectShowUploadMoreButton } from '../../selectors';
import SVG_ICONS from '~/assets/icons/icons';

const ActionsContainer = styled.div`
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  gap: ${globalTheme.spacing.sm};
`;

const {
  [rootAts.SET_QUEUE_ITEMS_RETRY]: retryQueueItemsAction,
  [rootAts.SET_QUEUE_ITEMS_ABORT]: abortQueueItemsAction,
  [rootAts.SET_WIDGET_VIEW_TYPE]: setWidgetViewTypeAction,
  [rootAts.DO_CLOSE_WIDGET]: closeWidgetAction,
} = actions.creators;

const LINK_ICON_SIZE = 'xs';

const AbortAllButton = ({ activeCount, linkFontSize }) => {
  const dispatch = useDispatch();

  return activeCount ? (
    <LinkButton
      text={t('queue.abort_all')}
      className="ml3"
      icon={SVG_ICONS.PAUSE}
      dataTest="abort-all-btn"
      onClick={() => dispatch(abortQueueItemsAction())}
      fontSize={linkFontSize}
      iconSize={LINK_ICON_SIZE}
    />
  ) : null;
};

const RetryAllButton = ({ failedCount, linkFontSize }) => {
  const dispatch = useDispatch();

  return failedCount > 0 ? (
    <LinkButton
      dataTest="retry-all-btn"
      text={t('queue.retry_failed')}
      className="ml3"
      icon={SVG_ICONS.RETRY}
      onClick={() => dispatch(retryQueueItemsAction())}
      textColorName="link"
      fontSize={linkFontSize}
      iconSize={LINK_ICON_SIZE}
      tabIndex={0}
    />
  ) : null;
};

const ShowCompletedButton = ({ doneCount, onShowCompleted, linkFontSize }) => {
  const showCompletedButton = useSelector(selectShowCompletedButton);

  return doneCount > 0 && showCompletedButton ? (
    <LinkButton
      dataTest="show-completed-btn"
      text={t('queue.show_completed')}
      className="ml3"
      icon={SVG_ICONS.COMPLETE}
      onClick={onShowCompleted}
      textColorName="link"
      fontSize={linkFontSize}
      iconSize={LINK_ICON_SIZE}
      tabIndex={0}
    />
  ) : null;
};

const UploadMoreButton = ({ linkFontSize }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const showUploadMoreButton = useSelector(selectShowUploadMoreButton);
  const isMultiple = useSelector(selectIsMultipleEnabled);

  return isMultiple && showUploadMoreButton ? (
    <LinkButton
      dataTest="upload-btn"
      text={t('queue.upload_more')}
      className="mr3"
      icon={SVG_ICONS.UPLOAD_MORE}
      fontSize={linkFontSize}
      iconSize={LINK_ICON_SIZE}
      tabIndex={0}
      onClick={() =>
        dispatch(
          setWidgetViewTypeAction({
            type: WIDGET_VIEW_TYPES.INITIAL,
            router: { history },
          })
        )
      }
    />
  ) : null;
};

const DoneButton = ({ isDone, isInline }) => {
  const dispatch = useDispatch();

  return (
    <Button
      dataTest="queue-done"
      disabled={!isDone || isInline}
      colorName="action"
      icon={SVG_ICONS.CHECKED}
      text={t('queue.done')}
      tabIndex={0}
      onClick={() => dispatch(closeWidgetAction())}
    />
  );
};

const ExpandedQueueActionButtons = ({ activeCount, doneCount, failedCount, onShowCompleted, isDone, isInline }) => {
  const linkFontSize = useMixin(globalTheme.font.size.xxs);

  return (
    <ActionsContainer>
      <div>
        <UploadMoreButton />
        <DoneButton isDone={isDone} isInline={isInline} />
      </div>
      <div>
        <AbortAllButton activeCount={activeCount} linkFontSize={linkFontSize} />

        <RetryAllButton failedCount={failedCount} linkFontSize={linkFontSize} />

        <ShowCompletedButton doneCount={doneCount} linkFontSize={linkFontSize} onShowCompleted={onShowCompleted} />
      </div>
    </ActionsContainer>
  );
};

export default ExpandedQueueActionButtons;
